<template>
  <div class="order-tracker">
    <h2>{{ $t('orders.tracker_title') }}</h2>

    <div v-if="ordersStore.loading" class="loading" role="status" aria-live="polite">
      {{ $t('common.loading') }}
    </div>

    <div v-else-if="ordersStore.error" class="error" role="alert" aria-live="assertive">
      <p class="error-text">{{ ordersStore.error }}</p>
      <button class="btn-retry" type="button" @click="handleRetry">
        {{ $t('common.retry', 'Reintentar') }}
      </button>
    </div>

    <div v-else-if="trackedOrders.length === 0" class="no-orders" role="status" aria-live="polite">
      {{ $t('orders.no_active_orders') }}
    </div>

    <div v-else class="orders-list">
      <div
        v-for="order in trackedOrders"
        :key="order.id"
        class="order-card"
        :class="{ 'order-card--active': String(order.id) === props.activeOrderId }"
      >
        <div class="order-header">
          <h3>{{ $t('orders.order_number', { number: getDisplayId(order) }) }}</h3>
          <span class="order-status" :class="`status-${getOrderStatus(order)}`" role="status" aria-live="polite">
            {{ getOrderStatusText(getOrderStatus(order)) }}
          </span>
        </div>

        <div class="order-items">
          <div v-for="item in order.items" :key="item.id" class="order-item">
            <div class="item-info">
              <span class="item-name">{{ item.name }}</span>
              <span class="item-quantity">x{{ item.quantity }}</span>
            </div>
            <div v-if="item.extras && item.extras.length > 0" class="item-extras">
              <span v-for="extra in item.extras" :key="extra" class="item-extra">
                + {{ extra }}
              </span>
            </div>
          </div>
        </div>

        <div class="order-footer">
          <div class="order-total">
            {{ formatCurrency(Number(order.total_amount || 0)) }}
          </div>
          <div class="order-actions">
            <button v-if="canEditOrder(order)" class="btn-secondary" @click="editOrder(String(order.id))">
              {{ $t('orders.edit') }}
            </button>
            <button v-if="canCancelOrder(order) && !isCancelling(order)" class="btn-danger" :disabled="isCancelling(order)" @click="cancelOrder(String(order.id))">
              {{ isCancelling(order) ? 'Cancelando...' : $t('orders.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useOrdersStore, type ClientOrder } from '../../stores/orders-store';
import { formatCurrency } from '@shared/utils/currency';
import {
  canClientCancelOrder,
  canClientEditOrder,
  getOrderStatusLabel,
  normalizeOrderStatus,
} from '@shared/workflow/status';

const props = withDefaults(
  defineProps<{
    activeOrderId?: string;
    readonly?: boolean;
  }>(),
  {
    activeOrderId: '',
    readonly: false,
  },
);

const emit = defineEmits<{
  (e: 'edit-order', orderId: string): void;
  (e: 'cancel-order', orderId: string): void;
}>();

const ordersStore = useOrdersStore();

const handleRetry = () => {
  void ordersStore.fetchActiveOrders();
};

const trackedOrders = computed(() => ordersStore.activeOrders);

const getOrderStatus = (order: ClientOrder): string => {
  return normalizeOrderStatus(order.workflow_status || order.status || '');
};

const getDisplayId = (order: ClientOrder): string => {
  const candidate = (order as any).order_number ?? order.display_order_id ?? order.display_id;
  if (candidate !== undefined && candidate !== null && String(candidate).trim() !== '') {
    return String(candidate).trim();
  }
  const rawId = String(order.id || '').trim();
  if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(rawId)) {
    return rawId.slice(0, 8);
  }
  return rawId;
};

const getOrderStatusText = (status: string): string => {
  return getOrderStatusLabel(status, 'client') || status;
};

const canEditOrder = (order: ClientOrder): boolean => {
  return !props.readonly && canClientEditOrder(getOrderStatus(order));
};

const canCancelOrder = (order: ClientOrder): boolean => {
  return !props.readonly && canClientCancelOrder(getOrderStatus(order));
};

const editOrder = (orderId: string) => {
  emit('edit-order', orderId);
};

const cancelOrder = (orderId: string) => {
  emit('cancel-order', orderId);
};

const isCancelling = (order: ClientOrder): boolean => {
  return ordersStore.cancellingOrderId === String(order.id);
};
</script>

<style scoped>
.order-tracker {
  padding: 16px;
}

.order-tracker h2 {
  margin: 0 0 16px 0;
  color: #333;
  font-size: 20px;
}

.loading,
.error,
.no-orders {
  text-align: center;
  padding: 32px 16px;
  color: #666;
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #ef4444;
}

.error-text {
  margin: 0;
  font-size: 15px;
}

.btn-retry {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-top: 8px;
  padding: 8px 20px;
  background-color: #ff6b35;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;
}

.btn-retry:hover {
  background-color: #e55a2b;
}

.btn-retry:active {
  transform: scale(0.98);
}

.btn-retry:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.4);
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  background: white;
  transition: border-color 0.2s;
}

.order-card--active {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.order-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.order-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-new {
  background: #fef3c7;
  color: #d97706;
}
.status-queued {
  background: #dcfce7;
  color: #16a34a;
}
.status-preparing {
  background: #dbeafe;
  color: #1d4ed8;
}
.status-ready {
  background: #ede9fe;
  color: #7c3aed;
}
.status-delivered {
  background: #f0fdf4;
  color: #16a34a;
}
.status-paid {
  background: #f0fdf4;
  color: #16a34a;
}
.status-cancelled {
  background: #fee2e2;
  color: #ef4444;
}

.order-items {
  margin-bottom: 12px;
}

.order-item {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.item-info {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.item-name {
  color: #333;
}

.item-quantity {
  color: #666;
}

.item-extras {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.item-extra {
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-total {
  font-weight: bold;
  font-size: 16px;
  color: #333;
}

.order-actions {
  display: flex;
  gap: 8px;
}

.btn-secondary {
  background: #6b7280;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:hover {
  background: #4b5563;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-danger:hover {
  background: #dc2626;
}
</style>
